import type { ModifiedPlayer, OddsResponse } from '@library/types/simulations';
import { useMemo, useState } from 'react';
import { Button } from '@library/components/buttons';
import type { LeagueTeamPlayer } from '@library/types/player';
import type { OverrideValidationError } from '@bo-hooks/simulations/useSimulationDetailSteps';
import { PlayerInputs } from '@bo-components/simulations/steps/StepOneBuild/PlayerInputs/PlayerInputs';
import type { Updater } from 'use-immer';
import chevronDownIcon from '@library/assets/images/icons/chevron-down-icon.svg';
import { classNames } from '@library/utils';
import { sortPlayersByPositionAndName } from '@bo-utils/simulations';
import { useGetPlayerInjuriesQuery } from '@bo-services/simulationsApi';

export const TeamPlayers = ({
  defaultPlayers,
  homeTeam,
  leagueId,
  modifiedPlayers,
  simulationOdds,
  overrideValidationErrors,
  removedPlayers,
  setModifiedPlayers,
  setOverrideValidationErrors,
  simulationId,
  teamName,
  teamPlayers
}: {
  defaultPlayers: Array<ModifiedPlayer>;
  leagueId: string;
  homeTeam: boolean;
  modifiedPlayers: Array<ModifiedPlayer>;
  simulationOdds: OddsResponse | undefined;
  overrideValidationErrors: Array<OverrideValidationError>;
  removedPlayers: Array<Omit<ModifiedPlayer, 'playerOverrides'>>;
  setModifiedPlayers: Updater<Array<ModifiedPlayer>>;
  setOverrideValidationErrors: Updater<Array<OverrideValidationError>>;
  simulationId: string;
  teamName: string;
  teamPlayers: Array<LeagueTeamPlayer>;
}) => {
  const [showPlayerOverrides, setShowPlayerOverrides] = useState(true);

  const { data } = useGetPlayerInjuriesQuery({
    league_id: leagueId,
    team: teamName
  });

  const filteredSortedPlayers = useMemo(() => {
    const filteredPlayers = teamPlayers.filter(
      (player) =>
        !removedPlayers.some(
          (removedPlayer) =>
            removedPlayer.foreignId === player.foreign_id &&
            removedPlayer.homeTeam === homeTeam
        )
    );

    return sortPlayersByPositionAndName({ players: filteredPlayers });
  }, [homeTeam, teamPlayers, removedPlayers]);

  return (
    <div
      className={classNames(
        'border-soFresh-140 inline-flex w-full flex-col items-start justify-start rounded border'
      )}
    >
      <div className="bg-soFresh-120 border-soFresh-140 font-heebo text-soClean-100 box-border flex w-full flex-row items-center justify-between rounded p-3 text-base font-medium leading-6">
        <span>Team Roster</span>
        <div className="flex gap-4">
          <Button
            type="button"
            title="ToggleGameTeamPlayersButton"
            onClick={() => setShowPlayerOverrides(!showPlayerOverrides)}
          >
            <img
              alt="Chevron Down icon"
              className={classNames(showPlayerOverrides ? 'rotate-180' : '')}
              src={chevronDownIcon}
            />
          </Button>
        </div>
      </div>
      {showPlayerOverrides && (
        <div className="border-soFresh-140 grid h-[380px] w-full grid-cols-[max-content_max-content] gap-2 overflow-scroll overscroll-x-none py-2">
          {filteredSortedPlayers.map((player) => (
            <PlayerInputs
              defaultPlayers={defaultPlayers}
              homeTeam={homeTeam}
              injury={data?.players.find(
                (injury) => injury.player.id === player.foreign_id
              )}
              key={player.foreign_id}
              modifiedPlayers={modifiedPlayers}
              simulationOdds={simulationOdds}
              overrideValidationErrors={overrideValidationErrors}
              player={player}
              setModifiedPlayers={setModifiedPlayers}
              setOverrideValidationErrors={setOverrideValidationErrors}
              simulationId={simulationId}
            />
          ))}
        </div>
      )}
    </div>
  );
};
